import { useId } from "react";
import { formatDistanceKm } from "@hunch/core";
import styles from "./SliderEstimateEngine.module.css";

export interface SliderEstimateEngineProps {
  min: number;
  max: number;
  value: number;
  onChange: (km: number) => void;
  disabled?: boolean;
  ariaLabel: string;
  lang: "de" | "en";
}

const RAW_MAX = 1000;

function kmToRaw(km: number, min: number, max: number): number {
  const t = Math.log(km / min) / Math.log(max / min);
  return Math.round(t * RAW_MAX);
}

function rawToKm(raw: number, min: number, max: number): number {
  const t = raw / RAW_MAX;
  return min * Math.pow(max / min, t);
}

// Fixed marks per spec §8.3: 10/100/1000/10000/20000 km on a 10-20000 km
// slider. Their log-scale positions are therefore constants, not runtime
// data — precomputed CSS classes avoid needing an inline `style` attribute
// (which the strict style-src CSP, with no 'unsafe-inline', would block).
const MARKS: Array<{ km: number; className: keyof typeof styles }> = [
  { km: 10, className: "markAt0" },
  { km: 100, className: "markAt30" },
  { km: 1000, className: "markAt61" },
  { km: 10000, className: "markAt91" },
  { km: 20000, className: "markAt100" },
];

/**
 * Short label for a fixed mark ("1k"/"10k"/"20k") instead of the locale
 * thousands-grouped form ("1.000"/"10.000"/"20.000") — the two rightmost
 * marks sit only 9% apart on the log scale, too close for the longer form
 * to fit on one line without overlapping or wrapping to a second line.
 */
function markLabel(km: number): string {
  return km >= 1000 ? `${km / 1000}k` : String(km);
}

export function SliderEstimateEngine({
  min,
  max,
  value,
  onChange,
  disabled = false,
  ariaLabel,
  lang,
}: SliderEstimateEngineProps) {
  const id = useId();
  const raw = kmToRaw(value, min, max);

  return (
    <div className={styles.wrapper}>
      <div className={styles.valueDisplay}>{formatDistanceKm(value, lang)} km</div>
      <input
        id={id}
        type="range"
        className={styles.slider}
        min={0}
        max={RAW_MAX}
        value={raw}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => onChange(Math.round(rawToKm(Number(e.target.value), min, max)))}
      />
      <div className={styles.marks}>
        {MARKS.map((m) => (
          <span key={m.km} className={`${styles.mark} ${styles[m.className]}`}>
            {markLabel(m.km)}
          </span>
        ))}
      </div>
    </div>
  );
}
